<script setup>
/**
 * Two app screens in phone frames, fanned, with notification cards floating
 * over them. Drawn in CSS around real screenshots, for pages that have no
 * composed device image of their own (the studio page has two_devices.png).
 *
 * No filters anywhere in here: a filter under the notification cards left a
 * pale box around them on iPhones (see motion.css).
 */
import IconCalendar from "../icons/IconCalendar.vue";
import IconBell from "../icons/IconBell.vue";
import IconCheck from "../icons/IconCheck.vue";

defineProps({
  // [front, back], each { src, alt }
  screens: { type: Array, required: true },
  // each { kind: "booked" | "reminder", title, text }
  notifications: { type: Array, default: () => [] },
});
</script>

<template>
  <div class="phones">

    <figure
      v-for="(screen, i) in screens.slice(0, 2)"
      :key="screen.src"
      class="phone"
      :class="i === 0 ? 'phone--front' : 'phone--back'"
    >
      <img
        :src="screen.src"
        :alt="screen.alt"
        width="300"
        height="650"
        :fetchpriority="i === 0 ? 'high' : undefined"
      />
    </figure>

    <!-- Decoration: the copy beside the hero already says what these show. -->
    <div
      v-for="(note, i) in notifications"
      :key="note.title"
      class="note"
      :class="`note--${i}`"
      :style="{ '--note-index': i }"
      aria-hidden="true"
    >
      <span class="note__icon" :class="`note__icon--${note.kind}`">
        <IconBell v-if="note.kind === 'reminder'" />
        <IconCalendar v-else />
      </span>
      <span class="note__body">
        <strong>{{ note.title }}</strong>
        <span>{{ note.text }}</span>
      </span>
      <span v-if="note.kind === 'booked'" class="note__badge"><IconCheck /></span>
    </div>
  </div>
</template>

<style scoped>
.phones {
  position: relative;
  display: grid;
  place-items: center;
  inline-size: min(100%, 480px);
  aspect-ratio: 1 / 1.08;
}

/* ---------------------------------------------------------------- phones */

.phone {
  position: absolute;
  margin: 0;
  inline-size: 52%;
  padding: 6px;
  border-radius: 34px;
  background: var(--ink);
  box-shadow: var(--lift-3);
}

.phone img {
  display: block;
  inline-size: 100%;
  block-size: auto;
  border-radius: 28px;
}

/* RTL: the front phone sits on the reading side, the back one behind it. */
.phone--front {
  z-index: 2;
  inset-inline-start: 8%;
  inset-block-start: 4%;
  transform: rotate(4deg);
}

.phone--back {
  z-index: 1;
  inset-inline-end: 6%;
  inset-block-start: 12%;
  transform: rotate(-7deg) scale(0.92);
  opacity: 0.96;
}

/* --------------------------------------------------------- notifications */

.note {
  position: absolute;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  inline-size: min(76%, 270px);
  padding: var(--space-3);
  border: var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--lift-3);
  /* They arrive the way a phone shows them: one after the other. */
  animation: note-in 700ms var(--ease-spring) both;
  animation-delay: calc(900ms + var(--note-index) * 900ms);
}

@keyframes note-in {
  from {
    opacity: 0;
    transform: translate3d(0, 18px, 0) scale(0.9);
  }
}

/* The confirmation's check lands just after its card. */
.note__badge {
  animation: badge-in 500ms var(--ease-spring) both;
  animation-delay: calc(1400ms + var(--note-index) * 900ms);
}

@keyframes badge-in {
  from {
    transform: scale(0);
  }
}

/* Low, over the back phone's action bar: the screens' own appointment cards
   sit around two-thirds down and should stay visible. */
.note--0 {
  inset-inline-end: 0;
  inset-block-end: -3%;
}

.note--1 {
  inset-inline-start: -2%;
  inset-block-start: 10%;
  inline-size: min(66%, 240px);
}

.note__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: 38px;
  block-size: 38px;
  border-radius: var(--radius-md);
  background: var(--brand-soft);
  color: var(--brand);
}

.note__icon--reminder {
  background: var(--accent-soft);
  color: var(--accent);
}

.note__icon :deep(svg) {
  inline-size: 19px;
  block-size: 19px;
}

.note__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-inline-size: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-snug);
}

.note__body strong {
  color: var(--ink);
}

.note__body span {
  font-size: var(--text-xs);
  color: var(--ink-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.note__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: 24px;
  block-size: 24px;
  border-radius: 50%;
  background: var(--success-soft);
  color: var(--success);
}

.note__badge :deep(svg) {
  inline-size: 13px;
  block-size: 13px;
  stroke-width: 3;
}

@media (max-width: 899px) {
  .phones {
    inline-size: min(100%, 360px);
  }

  .note {
    padding: var(--space-2) var(--space-3);
  }

  .note__icon {
    inline-size: 32px;
    block-size: 32px;
  }
}
</style>
